import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import EmployeeRecords from "./pages/EmployeeRecords";
import EmployeeProfile from "./pages/EmployeeProfile";
import Announcements from "./pages/Announcements";
import AdminDashboard from "./pages/AdminDashboard";
import AdminEmployees from "./pages/AdminEmployees";
import AdminRecords from "./pages/AdminRecords";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import AdminProjects from "./pages/AdminProjects";
import AdminCertifications from "./pages/AdminCertifications";
import AdminDocuments from "./pages/AdminDocuments";
import AdminInvites from "./pages/AdminInvites";
import AdminPendingApprovals from "./pages/AdminPendingApprovals";
import AdminEquipment from "./pages/AdminEquipment";
import AdminTimeOff from "./pages/AdminTimeOff";
import AdminApprovals from "./pages/AdminApprovals";
import AdminEmployeeRecords from "./pages/AdminEmployeeRecords";
import AdminEquipmentInspections from "./pages/AdminEquipmentInspections";
import AdminServiceRecords from "./pages/AdminServiceRecords";
import AdminOutOfService from "./pages/AdminOutOfService";
import AdminServiceRequests from "./pages/AdminServiceRequests";
import AdminSpillReports from "./pages/AdminSpillReports";
import AdminVehicleInspections from "./pages/AdminVehicleInspections";
import AdminHandToolInspections from "./pages/AdminHandToolInspections";
import AdminPPEInspections from "./pages/AdminPPEInspections";
import AdminLiveTracking from "./pages/AdminLiveTracking";
import AdminPayroll from "./pages/AdminPayroll";
import Settings from "./pages/Settings";
import EmployeeIncidents from "./pages/EmployeeIncidents";
import AdminIncidentReports from "./pages/AdminIncidentReports";
import AdminToolboxTalks from "./pages/AdminToolboxTalks";
import AdminFLRA from "./pages/AdminFLRA";
import { Loader2 } from "lucide-react";

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Auth Route - redirect if already logged in
function AuthRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }

  return children;
}

// Root redirect
function RootRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Root redirect */}
          <Route path="/" element={<RootRedirect />} />

          {/* Auth routes */}
          <Route path="/login" element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          } />
          
          {/* Public registration route */}
          <Route path="/register" element={<Register />} />

          {/* Employee routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <EmployeeDashboard />
            </ProtectedRoute>
          } />
          <Route path="/records" element={
            <ProtectedRoute>
              <EmployeeRecords />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <EmployeeProfile />
            </ProtectedRoute>
          } />
          <Route path="/announcements" element={
            <ProtectedRoute>
              <Announcements />
            </ProtectedRoute>
          } />

          {/* Shared Settings (admin + employee) */}
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />

          {/* Admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/live-tracking" element={
            <ProtectedRoute adminOnly>
              <AdminLiveTracking />
            </ProtectedRoute>
          } />
          <Route path="/admin/employees" element={
            <ProtectedRoute adminOnly>
              <AdminEmployees />
            </ProtectedRoute>
          } />
          <Route path="/admin/records" element={
            <ProtectedRoute adminOnly>
              <AdminRecords />
            </ProtectedRoute>
          } />
          <Route path="/admin/payroll" element={
            <ProtectedRoute adminOnly>
              <AdminPayroll />
            </ProtectedRoute>
          } />
          <Route path="/admin/announcements" element={
            <ProtectedRoute adminOnly>
              <AdminAnnouncements />
            </ProtectedRoute>
          } />
          <Route path="/admin/projects" element={
            <ProtectedRoute adminOnly>
              <AdminProjects />
            </ProtectedRoute>
          } />
          <Route path="/admin/certifications" element={
            <ProtectedRoute adminOnly>
              <AdminCertifications />
            </ProtectedRoute>
          } />
          <Route path="/admin/documents" element={
            <ProtectedRoute adminOnly>
              <AdminDocuments />
            </ProtectedRoute>
          } />
          <Route path="/admin/invites" element={
            <ProtectedRoute adminOnly>
              <AdminInvites />
            </ProtectedRoute>
          } />
          <Route path="/admin/pending-approvals" element={
            <ProtectedRoute adminOnly>
              <AdminPendingApprovals />
            </ProtectedRoute>
          } />
          <Route path="/admin/equipment" element={
            <ProtectedRoute adminOnly>
              <AdminEquipment />
            </ProtectedRoute>
          } />
          <Route path="/admin/time-off" element={
            <ProtectedRoute adminOnly>
              <AdminTimeOff />
            </ProtectedRoute>
          } />
          <Route path="/admin/approvals" element={
            <ProtectedRoute adminOnly>
              <AdminApprovals />
            </ProtectedRoute>
          } />
          <Route path="/admin/employee-records" element={
            <ProtectedRoute adminOnly>
              <AdminEmployeeRecords />
            </ProtectedRoute>
          } />
          <Route path="/admin/equipment-inspections" element={
            <ProtectedRoute adminOnly>
              <AdminEquipmentInspections />
            </ProtectedRoute>
          } />
          <Route path="/admin/service-records" element={
            <ProtectedRoute adminOnly>
              <AdminServiceRecords />
            </ProtectedRoute>
          } />
          <Route path="/admin/out-of-service" element={
            <ProtectedRoute adminOnly>
              <AdminOutOfService />
            </ProtectedRoute>
          } />
          <Route path="/admin/service-requests" element={
            <ProtectedRoute adminOnly>
              <AdminServiceRequests />
            </ProtectedRoute>
          } />
          <Route path="/admin/spill-reports" element={
            <ProtectedRoute adminOnly>
              <AdminSpillReports />
            </ProtectedRoute>
          } />
          <Route path="/admin/vehicle-inspections" element={
            <ProtectedRoute adminOnly>
              <AdminVehicleInspections />
            </ProtectedRoute>
          } />
          <Route path="/admin/hand-tool-inspections" element={
            <ProtectedRoute adminOnly>
              <AdminHandToolInspections />
            </ProtectedRoute>
          } />
          <Route path="/admin/ppe-inspections" element={
            <ProtectedRoute adminOnly>
              <AdminPPEInspections />
            </ProtectedRoute>
          } />

          {/* Employee time off */}
          <Route path="/time-off" element={
            <ProtectedRoute>
              <AdminTimeOff />
            </ProtectedRoute>
          } />
          <Route path="/incidents" element={
            <ProtectedRoute>
              <EmployeeIncidents />
            </ProtectedRoute>
          } />
          <Route path="/service-requests" element={
            <ProtectedRoute>
              <AdminServiceRequests />
            </ProtectedRoute>
          } />
          <Route path="/vehicle-inspections" element={
            <ProtectedRoute>
              <AdminVehicleInspections />
            </ProtectedRoute>
          } />
          <Route path="/hand-tool-inspections" element={
            <ProtectedRoute>
              <AdminHandToolInspections />
            </ProtectedRoute>
          } />
          <Route path="/ppe-inspections" element={
            <ProtectedRoute>
              <AdminPPEInspections />
            </ProtectedRoute>
          } />
          <Route path="/out-of-service" element={
            <ProtectedRoute>
              <AdminOutOfService />
            </ProtectedRoute>
          } />
          <Route path="/spill-reports" element={
            <ProtectedRoute>
              <AdminSpillReports />
            </ProtectedRoute>
          } />
          <Route path="/admin/incident-reports" element={
            <ProtectedRoute adminOnly>
              <AdminIncidentReports />
            </ProtectedRoute>
          } />
          <Route path="/admin/toolbox-talks" element={
            <ProtectedRoute adminOnly>
              <AdminToolboxTalks />
            </ProtectedRoute>
          } />
          <Route path="/admin/flra" element={
            <ProtectedRoute adminOnly>
              <AdminFLRA />
            </ProtectedRoute>
          } />
          <Route path="/toolbox-talks" element={
            <ProtectedRoute>
              <AdminToolboxTalks />
            </ProtectedRoute>
          } />
          <Route path="/flra" element={
            <ProtectedRoute>
              <AdminFLRA />
            </ProtectedRoute>
          } />
          <Route path="/my-records" element={
            <ProtectedRoute>
              <EmployeeRecords />
            </ProtectedRoute>
          } />

          {/* Catch all - redirect to root */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
